// Tick phase 7 (IMPLEMENTATION §2.4): discoveries, then unlocks, goals and tutorial actions, then stall grace
// transitions.
//   Discoveries are recorded where items are created (entities.discover; M2 decision 12).
//   Readout streaks: when a readout bucket closes, each Gallery binding that holds (R9 status over the
//   closed window) extends its streak by one bucket, else the streak restarts (galleryBinding triggers).
//   Unlocks: the trigger engine (unlocks.fireUnlocks); the goal chip moves with them (applyUnlock).
//   Shop reveals: an unlocked item joins progress.seen the first time cash reaches its reveal share, or at
//   once when it is the goal chip's shop target (rules/price revealDue; `reveal` event for the Shop badge).
//   Tutorial: tutorial.runTutorial (tips shown / retired, `hint` events).
//   Sink stalls that depend on fill state (CRATE_FULL) are refreshed, then the stall transitions run (one
//   `stall` event per episode after graceTicks).
import type { Ctx } from '../ctx.ts';
import { isActive } from '../defs.ts';
import { closesBucket } from '../readout.ts';
import { crateTotal } from '../rules/accept.ts';
import { galleryStatus } from '../rules/galleryStatus.ts';
import { revealDue } from '../rules/price.ts';
import { clearStall, raiseStall, runStallTransitions } from '../stall.ts';
import { activeTip, runTutorial } from '../tutorial.ts';
import { fireUnlocks } from '../unlocks.ts';

function crateStalls(c: Ctx): void {
  const caps = c.d.buildings.crate.capacity;
  for (const b of c.s.buildings) {
    if (b.type !== 'crate' || b.ghost !== null || b.paused) continue;
    if (crateTotal(b) >= (caps[b.level - 1] ?? 0)) raiseStall(c, b, 'CRATE_FULL');
    else if (b.stall === 'CRATE_FULL') clearStall(c, b);
  }
}

/** Binding streaks, sampled when a readout bucket closes (the binding only changes then). */
function bindingStreaks(c: Ctx): void {
  if (!closesBucket(c.d, c.s.tick)) return;
  let starved = false;
  let gunLimited = false;
  for (const b of c.s.buildings) {
    if (b.type !== 'gallery' || b.ghost !== null || b.paused) continue;
    const binding = galleryStatus(c.d, b).binding;
    if (binding === 'starved') starved = true;
    if (binding === 'gunLimited') gunLimited = true;
  }
  const step = c.d.economy.galleryReadout.bucketTicks;
  const st = c.s.progress.bindingT;
  st.starved = starved ? st.starved + step : 0;
  st.gunLimited = gunLimited ? st.gunLimited + step : 0;
}

function revealShop(c: Ctx): void {
  const seen = c.s.progress.seen;
  for (const item of c.d.shop) {
    if (!isActive(c.d, item.version) || seen.includes(item.id)) continue;
    if (!revealDue(c.s, c.d, item.id)) continue;
    seen.push(item.id);
    c.ev.push({ e: 'reveal', item: item.id });
  }
}

export function runProgress(c: Ctx): void {
  const tip = activeTip(c.s, c.d);
  bindingStreaks(c);
  fireUnlocks(c);
  revealShop(c);
  runTutorial(c, tip === null ? null : tip.id);
  crateStalls(c);
  runStallTransitions(c);
}
